import { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { ArrowUDownLeftIcon } from '@phosphor-icons/react'

import { BackListButton, Loading } from '@reveni-io/reveni-ui'

import FiltersSection from '@/common/components/FiltersSection/FiltersSection'
import AppContext from '@/common/context/app.context'
import useRegions from '@/common/hooks/useRegions/useRegions'
import useReturnPortalFeatures from '@/common/hooks/useReturnPortalFeatures/useReturnPortalFeatures'
import MostReturnedCard from '@/metrics/components/MostReturnedCard/MostReturnedCard'
import Product from '@/metrics/components/Product/Product'
import VariantsCard from '@/metrics/components/VariantsCard/VariantsCard'
import useFilters from '@/metrics/hooks/useFilters/useFilters'
import { entityTypes } from '@/metrics/hooks/useMetrics/constants'
import useMetrics from '@/metrics/hooks/useMetrics/useMetrics'
import { getReasonSummaryOrdering } from '@/metrics/pages/ReasonMetricDetailPage/helpers'
import useReasonSummary from '@/metrics/pages/ReasonMetricDetailPage/hooks/useReasonSummary/useReasonSummary'
import metricsServices from '@/metrics/services/services'
import routes from '@/Router/routes.constants'

import * as Styles from './styles'

const ProductByReasonMetricsDetailPage = ({ isUnifiedDashboard }) => {
  const { reasonKey, productPosition } = useParams()
  const { info } = useContext(AppContext)
  const { t } = useTranslation()
  const { regions } = useRegions()

  const { commonFilters, setCommonFilters, getUrlSearchParams, productsFilters, reasonsFilters } = useFilters()
  const { returnPortalFeatures } = useReturnPortalFeatures({ returnPortalId: info?.selectedReturnPortal?.id })
  const salesOrdersDataActivated = returnPortalFeatures?.sales_orders_data_activated

  const returnPortalId = isUnifiedDashboard ? null : info?.selectedReturnPortal?.id

  const { reasonSummary } = useReasonSummary({
    reasonKey,
    filters: commonFilters,
    ordering: getReasonSummaryOrdering({
      reasonsFilters,
      configurationPreferences: returnPortalFeatures,
      isUnifiedDashboard,
    }),
    returnPortalId,
  })

  const { data: reasonTopReturnedProducts, loading: loadingReasonTopReturnedProducts } = useMetrics({
    returnPortalId,
    service: metricsServices.getReasonTopReturnedProducts,
    entityId: reasonKey,
    entityType: entityTypes.reason,
    filters: commonFilters,
    additionalFilters: {
      ordering: productsFilters?.ordering,
    },
  })

  const product = reasonTopReturnedProducts?.results?.[productPosition]

  const { data: variants, loading: loadingVariants } = useMetrics({
    returnPortalId,
    service: metricsServices.getProductTopVariants,
    entityId: product?.id,
    entityType: entityTypes.product,
    filters: commonFilters,
    additionalFilters: {
      reasonId: reasonKey,
    },
    doRequest: !!product?.id,
  })

  const reasonPath = isUnifiedDashboard
    ? routes.merchantDashboardReasonMetricsDetailPage
    : routes.reasonMetricsDetailPage

  const dashboardPath = isUnifiedDashboard ? routes.merchantDashboardPage : routes.dashboardPage

  const getDashboardUrl = () => {
    return `${dashboardPath}?${getUrlSearchParams()}`
  }

  const getBreadcrumbs = () => {
    return [
      {
        title: reasonSummary?.position ? `#${reasonSummary.position} ${t('Motivo')}` : t('Motivo'),
        subtitle: reasonSummary?.display,
        url: `${reasonPath.replace(':reasonKey', reasonKey)}?${getUrlSearchParams()}`,
      },
      {
        title: `#${Number(productPosition) + 1} ${t('Producto')}`,
        subtitle: product?.name,
      },
    ]
  }

  return (
    <Styles.Container>
      <Styles.HeaderContainer>
        <BackListButton to={getDashboardUrl()}>{t('Volver al dashboard')}</BackListButton>
        <FiltersSection regions={regions} filters={commonFilters} onChange={setCommonFilters} />
      </Styles.HeaderContainer>
      <MostReturnedCard
        Icon={ArrowUDownLeftIcon}
        title={t('Motivos')}
        loading={loadingReasonTopReturnedProducts}
        item={product && <Product product={{ ...product, index: Number(productPosition) + 1 }} showAmounts={false} />}
        count={product?.count}
        percentageText={t('Motivo')}
        countText={t('Unidades devueltas')}
        percentage={product?.percentage}
        breadcrumbs={getBreadcrumbs()}
        sales={product?.sales}
        salesOrdersDataActivated={salesOrdersDataActivated || isUnifiedDashboard}
      />
      {variants && (
        <Loading show={loadingVariants || loadingReasonTopReturnedProducts} height="350px">
          <VariantsCard
            data={variants?.results}
            loading={loadingVariants}
            salesOrdersDataActivated={salesOrdersDataActivated || isUnifiedDashboard}
          />
        </Loading>
      )}
    </Styles.Container>
  )
}

export default ProductByReasonMetricsDetailPage
